import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSettings } from '../../contexts/SettingsContext';

/**
 * DataTable Component
 * Reusable table component for displaying tabular data
 * Uses user preference for items per page
 * @param {Object} props - Component props
 * @param {Array<Object>} props.data - Array of data objects to display
 * @param {Array<Object>} props.columns - Column definitions [{ key, label, render }]
 * @param {string} [props.title] - Table title
 * @param {boolean} [props.loading] - Loading state
 * @param {number} [props.itemsPerPage] - Items per page for pagination (uses user preference if not provided)
 * @param {boolean} [props.searchable] - Enable search
 * @param {boolean} [props.sortable] - Enable sorting
 */
const DataTable = ({
  data = [],
  columns = [],
  title = '',
  loading = false,
  itemsPerPage = null,
  searchable = true,
  sortable = true
}) => {
  // Get settings from context
  const settings = useSettings();
  
  // Use user preference if itemsPerPage not provided
  const defaultItemsPerPage = itemsPerPage || settings.tableItemsPerPage;
  const effectiveItemsPerPage = itemsPerPage !== null ? itemsPerPage : defaultItemsPerPage;
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter and sort data
  const processedData = useMemo(() => {
    let result = [...data];

    // Apply search
    if (searchable && searchTerm) {
      result = result.filter(item =>
        columns.some(col => {
          const value = item[col.key];
          return value && String(value).toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply sorting
    if (sortable && sortColumn) {
      result.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        
        if (aVal === bVal) return 0;
        const comparison = aVal > bVal ? 1 : -1;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchTerm, sortColumn, sortDirection, columns, searchable, sortable]);

  // Pagination - use effective items per page
  const totalPages = Math.ceil(processedData.length / effectiveItemsPerPage);
  const startIndex = (currentPage - 1) * effectiveItemsPerPage;
  const endIndex = startIndex + effectiveItemsPerPage;
  const paginatedData = processedData.slice(startIndex, endIndex);

  const handleSort = (columnKey) => {
    if (!sortable) return;
    
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <div className="data-table loading">
        <div className="table-loading">
          <div className="loading-spinner" />
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="data-table empty">
        <div className="table-empty">
          <i className="bi bi-inbox icon-brand"></i>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table">
      {title && (
        <div className="table-header">
          <h3>{title}</h3>
          {searchable && (
            <div className="table-search">
              <i className="bi bi-search icon-brand"></i>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
            </div>
          )}
        </div>
      )}

      <div className="table-wrapper">
        <table className="table-content">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={sortable && sortColumn === column.key ? `sortable active ${sortDirection}` : (sortable ? 'sortable' : '')}
                  onClick={() => handleSort(column.key)}
                >
                  {column.label}
                  {sortable && (
                    <span className="sort-indicator">
                      {sortColumn === column.key ? (
                        sortDirection === 'asc' ? (
                          <i className="bi bi-chevron-up icon-brand"></i>
                        ) : (
                          <i className="bi bi-chevron-down icon-brand"></i>
                        )
                      ) : (
                        <i className="bi bi-chevron-expand icon-brand"></i>
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr key={row.id || index}>
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.render
                      ? column.render(row[column.key], row)
                      : row[column.key] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="table-pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <i className="bi bi-chevron-left icon-brand"></i>
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages} ({processedData.length} items)
          </span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            <i className="bi bi-chevron-right icon-brand"></i>
          </button>
        </div>
      )}
    </div>
  );
};

DataTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func
    })
  ).isRequired,
  title: PropTypes.string,
  loading: PropTypes.bool,
  itemsPerPage: PropTypes.number,
  searchable: PropTypes.bool,
  sortable: PropTypes.bool
};

export default DataTable;

